import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import clsx from "clsx";

interface Props {
  // Valor seleccionado en formato YYYY-MM-DD.
  value: string;
  onChange: (date: string) => void;
  // Set de dias YYYY-MM-DD que tienen grabaciones (se pintan con un punto verde).
  // El componente pide los dias del mes actualmente visible via onMonthChange.
  availableDays: Set<string>;
  // Llamado cuando cambia el mes visible (year, month0-based) — el padre puede
  // refetchear `availableDays` para el nuevo mes.
  onMonthChange?: (year: number, month: number) => void;
  loading?: boolean;
  className?: string;
}

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

function isoDate(year: number, month0: number, day: number): string {
  return `${year}-${pad2(month0 + 1)}-${pad2(day)}`;
}

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const DAY_NAMES_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

export function Calendar({ value, onChange, availableDays, onMonthChange, loading, className }: Props) {
  // Parse del valor seleccionado para inicializar el mes visible.
  const parsed = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (m) return { y: +m[1], mo: +m[2] - 1, d: +m[3] };
    const now = new Date();
    return { y: now.getFullYear(), mo: now.getMonth(), d: now.getDate() };
  }, [value]);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsed.y);
  const [viewMonth, setViewMonth] = useState(parsed.mo);
  const popRef = useRef<HTMLDivElement | null>(null);
  // Trackeamos el ultimo (year, month) que ya notificamos al padre para no
  // disparar onMonthChange duplicado cuando se reabre el popup en el mismo mes.
  const lastNotifiedRef = useRef<{ y: number; mo: number } | null>(null);

  // Cuando el calendario navega a un nuevo mes (o se abre por primera vez en
  // un mes distinto al ya conocido por el padre), avisar para que refetchee.
  useEffect(() => {
    if (!onMonthChange) return;
    const last = lastNotifiedRef.current;
    if (last && last.y === viewYear && last.mo === viewMonth) return;
    lastNotifiedRef.current = { y: viewYear, mo: viewMonth };
    onMonthChange(viewYear, viewMonth);
  }, [viewYear, viewMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click fuera = cerrar.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Grilla del mes: cuantos dias y en que dia de semana cae el 1.
  const firstDow = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // 0=lunes
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const todayIso = useMemo(() => {
    const n = new Date();
    return isoDate(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const prevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) { setViewYear((y) => y - 1); return 11; }
      return m - 1;
    });
  };
  const nextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) { setViewYear((y) => y + 1); return 0; }
      return m + 1;
    });
  };

  const pickDay = (d: number) => {
    onChange(isoDate(viewYear, viewMonth, d));
    setOpen(false);
  };

  // Renderizar cells: huecos antes del dia 1 + dias del mes.
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className={clsx("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 bg-bg border border-line rounded px-2 py-1.5 text-sm text-text hover:border-accent focus:outline-none focus:border-accent"
      >
        <CalIcon size={14} />
        <span>{value || "Elegir fecha..."}</span>
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute z-50 mt-1 bg-panel border border-line rounded shadow-lg p-2 w-64"
        >
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={prevMonth} className="p-1 hover:bg-bg rounded" aria-label="Mes anterior">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium">
              {MONTH_NAMES[viewMonth]} {viewYear}
              {loading && <span className="text-muted text-xs ml-2">cargando...</span>}
            </span>
            <button type="button" onClick={nextMonth} className="p-1 hover:bg-bg rounded" aria-label="Mes siguiente">
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-muted mb-1">
            {DAY_NAMES_SHORT.map((d) => <div key={d}>{d}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, idx) => {
              if (d == null) return <div key={`e-${idx}`} className="h-8" />;
              const iso = isoDate(viewYear, viewMonth, d);
              const hasRec = availableDays.has(iso);
              const isSelected = iso === value;
              const isToday = iso === todayIso;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => pickDay(d)}
                  className={clsx(
                    "h-8 relative rounded text-xs flex items-center justify-center transition-colors",
                    isSelected
                      ? "bg-accent text-white"
                      : "hover:bg-bg text-text",
                    isToday && !isSelected && "ring-1 ring-accent/50",
                  )}
                >
                  {d}
                  {hasRec && (
                    <span
                      className={clsx(
                        "absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full",
                        isSelected ? "bg-white" : "bg-green-500",
                      )}
                      aria-label="hay grabaciones"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
